/**
 * User schemas — CRUD + listing.
 */

import { z } from 'zod';
import { emailSchema, idSchema, iso8601Schema, pageInput } from './common';
import { USER_ROLES } from '../reference/roles';

export const userRoleSchema = z.enum(USER_ROLES);

export const userOutputSchema = z.object({
  id: idSchema,
  email: emailSchema,
  displayName: z.string().min(1).max(120),
  role: userRoleSchema,
  status: z.enum(['ACTIVE', 'INVITED', 'DISABLED']),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
  lastLoginAt: iso8601Schema.nullable(),
});
export type UserOutput = z.infer<typeof userOutputSchema>;

export const createUserInputSchema = z.object({
  email: emailSchema,
  displayName: z.string().min(1).max(120),
  role: userRoleSchema,
  /** @deprecated Use the invite flow instead. Kept for compatibility. */
  initialPassword: z.string().min(12).max(200).optional(),
});
export type CreateUserInput = z.infer<typeof createUserInputSchema>;

export const inviteUserInputSchema = z.object({
  email: emailSchema,
  displayName: z.string().min(1).max(120),
  role: userRoleSchema,
});
export type InviteUserInput = z.infer<typeof inviteUserInputSchema>;

export const acceptInviteInputSchema = z.object({
  token: z.string().min(1),
  newPassword: z.string().min(12).max(200),
});
export type AcceptInviteInput = z.infer<typeof acceptInviteInputSchema>;

export const inviteTokenInputSchema = z.object({
  token: z.string().min(1),
});
export type InviteTokenInput = z.infer<typeof inviteTokenInputSchema>;

export const adminResetPasswordInputSchema = z.object({
  newPassword: z.string().min(12).max(200),
});
export type AdminResetPasswordInput = z.infer<typeof adminResetPasswordInputSchema>;

export const updateUserInputSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  role: userRoleSchema.optional(),
  status: z.enum(['ACTIVE', 'INVITED', 'DISABLED']).optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserInputSchema>;

export const listUsersInputSchema = pageInput.extend({
  role: userRoleSchema.optional(),
  status: z.enum(['ACTIVE', 'INVITED', 'DISABLED']).optional(),
});
export type ListUsersInput = z.infer<typeof listUsersInputSchema>;